import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
})
export class ModalComponent implements OnChanges {
  @Input() visible = false;
  @Input() mode: 'view' | 'edit' | 'add' | 'delete' = 'view';
  @Input() data: any = {};
  @Input() entityLabel = 'Record';

  // unified lookup map (from grid)
  private _lookups: Record<string, { label: string; value: any }[]> = {};

  @Input()
  set lookups(value: Record<string, { label: string; value: any }[]>) {
    if (!value) return;
    // replace internal store when new lookups arrive (hydrates dynamically)
    this._lookups = value;
    this.internalLookups = { ...this.internalLookups, ...value };
  }
  get lookups() {
    return this._lookups;
  }

  // internal store (includes both unified and xList-style inputs)
  private internalLookups: Record<string, { label: string; value: any }[]> = {};

  @Output() close = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<any>();

  confirmInput = '';
  keys: string[] = [];

  // placeholder for dynamic Angular inputs ending in "List"
  @Input()
  set dynamicLookupInput(value: any) {
    // no-op: allows Angular to bind arbitrary xList inputs
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.keys = Object.keys(this.data);
    }

    // capture dynamically bound lists like [accountList], [clientList], etc.
    for (const prop in changes) {
      if (prop.endsWith('List') && Array.isArray((this as any)[prop])) {
        this.internalLookups[prop] = (this as any)[prop];
      }
    }

    if (changes['mode']) {
      this.confirmInput = '';
    }
  }

  isReadonly(key: string): boolean {
    return key.startsWith('_') || key.endsWith('At');
  }

  isForeignKey(key: string): boolean {
    return key.endsWith('Id');
  }

  getLookupList(key: string) {
    const normalize = (k: string) => k.replace(/[_-]/g, '').toLowerCase();

    const candidates = Object.keys(this.internalLookups);
    const match = candidates.find(
      c => normalize(c) === normalize(`${key}List`) ||
          (key.endsWith('Id') && normalize(c) === normalize(`${key.slice(0, -2)}List`))
    );

    return match ? this.internalLookups[match] : [];
  }

  resolveDisplayValue(key: string): any {
    if (this.isForeignKey(key)) {
      const lookup = this.getLookupList(key);
      const match = lookup.find((opt) => opt.value === this.data[key]);
      return match ? match.label : this.data[key];
    }
    return this.data[key];
  }

  onClose(): void {
    this.visible = false;
    this.close.emit();
  }

  onConfirm(): void {
    this.confirm.emit(this.data);
    this.onClose();
  }
}